import { useState } from "react";
import { Link } from "react-router";
import { Button } from "primereact/button";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { getRailsClient, getScormPackageClient } from "sj-ui-toolkit/src/client";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { useHideMicroPaths, useHideTimeSpent } from "sj-ui-toolkit/src/integrations";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { downloadCsv } from "sj-ui-toolkit/src/utils/downloadFile";
import { sanitizeTestId } from "sj-ui-toolkit/src/utils/string";

import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReportConfig {
  name: string;
  text: string;
  textHideTimeSpent?: string;
  url?: string;
  s3?: boolean;
  downloadFn?: () => Promise<void>;
}

interface S3ReportResponse {
  response: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BASE_REPORTS: ReportConfig[] = [
  {
    name: "Report - Full Details",
    text: "The Full Details report displays information for all users across Levels, Paths, and Quests, including in-progress, paused, and passed statuses. This report is updated every 10 minutes.",
    url: "/api/internal_api/v1/admin/dashboard/csv_file_detail.csv",
    s3: true,
  },
  {
    name: "Report - Summary",
    text: "The Summary report only displays information for in-progress Paths and Quests. It does not include previous paths, levels, or quests. This report is updated upon export.",
    url: "/api/internal_api/v1/admin/dashboard/csv_file_summary.csv",
    s3: false,
  },
  {
    name: "Report - Usage",
    text: "The Usage report summarizes all lessons consumed at each Level in the platform, by quarter. This report is updated on export.",
    url: "/api/internal_api/v1/admin/reports/usage.csv",
    s3: false,
  },
  {
    name: "Report - Learning Swing",
    text: "The Learning Swing report displays the before rating, after rating, and knowledge increase percentages in the Learning Swing for each lesson. This report is updated on export.",
    url: "/api/internal_api/v1/admin/value_proposition/lessons.csv",
    s3: false,
  },
  {
    name: "Report - Level Ranking",
    text: "The Level Ranking report displays learning swing data by Level and includes the knowledge increase percentages. This report is updated on export.",
    url: "/api/internal_api/v1/admin/value_proposition/levels.csv",
    s3: false,
  },
  {
    name: "Report - Users",
    text: "The User report displays all user data, including points, email address, first name, last name, and other configured attributes. This report is updated on export. ",
    url: "/api/internal_api/v1/admin/users/download",
    s3: false,
  },
  {
    name: "Report - Lesson Attempts",
    text: "The Lesson Attempts report displays data for each attempt a learner makes to complete a lesson, the status of the lesson and quiz (if applicable), time spent, and additional details. This report is updated every 10 minutes.",
    textHideTimeSpent:
      "The Lesson Attempts report displays data for each attempt a learner makes to complete a lesson, the status of the lesson and quiz (if applicable), and additional details. This report is updated every 10 minutes.",
    url: "/api/internal_api/v1/admin/reports/lesson_attempts/download",
    s3: true,
  },
];

const MICRO_REPORTS: ReportConfig[] = [
  {
    name: "Report - Micro Paths",
    text: "The Micro Path report displays details like first and last name, email, Micro Path name, progress, time spent, and status. This report is updated every 10 minutes.",
    textHideTimeSpent:
      "The Micro Path report displays details like first and last name, email, Micro Path name, progress, and status. This report is updated every 10 minutes.",
    url: "/api/internal_api/v1/admin/reports/micro_roles/download",
    s3: true,
  },
  {
    name: "Report - Micro Assignments",
    text: "The Micro Assignments report displays information such as first name, last name, Micro Assignment name, due date, status, and time spent. This report is updated every 10 minutes.",
    textHideTimeSpent:
      "The Micro Assignments report displays information such as first name, last name, Micro Assignment name, due date, and status. This report is updated every 10 minutes.",
    url: "/api/internal_api/v1/admin/reports/micro_assignments/download",
    s3: true,
  },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const ReportsDownload = () => {
  useBreadcrumb({
    label: "Downloadable Reports",
    route: AdminAppRoutes.routes.reporting.routes.download.route,
    display: "none",
    resetTrail: true,
  });

  const { showToast } = useToast();
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  const hideMicroPaths = useHideMicroPaths();
  const hideTimeSpent = useHideTimeSpent();
  const currentTenantName = getCurrentSelectedTenant() ?? "";
  const { isRestrictedBundle } = useUserContext();

  const SCORM_REPORTS: ReportConfig[] = [
    {
      name: "SCORM Lesson Attempts",
      text: "The SCORM Lesson Attempts report displays data for each time a learner makes an attempt to complete a lesson. This report is updated on export.",
      downloadFn: async () => {
        const { data, error } =
          await getScormPackageClient().exportLessonAttempts(currentTenantName);
        if (error) throw error;
        downloadCsv(data as unknown as string, "scorm-lesson-attempts.csv");
      },
    },
  ];

  const applyTimeSpentText = (reportList: ReportConfig[]) =>
    hideTimeSpent
      ? reportList.map(r => ({ ...r, text: r.textHideTimeSpent ?? r.text }))
      : reportList;

  const reports = applyTimeSpentText([
    ...BASE_REPORTS,
    ...(hideMicroPaths ? [] : MICRO_REPORTS),
    ...(isRestrictedBundle ? [] : SCORM_REPORTS),
  ]);

  const downloadCSV = async (report: ReportConfig) => {
    setDownloading(prev => ({ ...prev, [report.name]: true }));

    try {
      if (report.downloadFn) {
        await report.downloadFn();
      } else if (report.s3) {
        const { data } = await getRailsClient().get<S3ReportResponse>(report.url!);
        const link = document.createElement("a");
        link.href = data.response;
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const { data } = await getRailsClient().get<string>(report.url!);
        downloadCsv(data, report.name);
      }
    } catch (error) {
      console.error(error);
      showToast({ severity: "error", summary: "Error", detail: "Failed to download report" });
    } finally {
      setDownloading(prev => ({ ...prev, [report.name]: false }));
    }
  };

  return (
    <>
      <div className="mb-4">
        <h1>Downloadable Reports</h1>
      </div>
      <p className="body-sm">
        Click{" "}
        <Link
          to={AdminAppRoutes.routes.connections.routes.reportingApi.route}
          className="font-semibold text-primary"
        >
          here
        </Link>{" "}
        to utilize the Security Journey reporting API.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-6">
        {reports.map((report, index) => (
          <div
            key={index}
            data-testid={`report-card-${sanitizeTestId(report.name)}`}
            className={`p-6 ${themeClasses.bgElevation1} rounded-md`}
          >
            <div className="flex flex-row items-center justify-between gap-4">
              <h3 className="atlas-h4 font-semibold mb-2">{report.name}</h3>
              <Button
                icon="pi pi-download"
                onClick={() => downloadCSV(report)}
                loading={downloading[report.name]}
                aria-label={`Download ${report.name}`}
                data-testid={`report-download-${sanitizeTestId(report.name)}`}
              />
            </div>
            <div className="mt-6">
              <p className={`body-sm ${themeClasses.textSecondary}`}>{report.text}</p>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default ReportsDownload;
